import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { DetailReadout } from "@/modules/shell/DetailReadout";
import { stashListReturn } from "@/modules/shell/list-return-state";
import { getParty } from "@/shared/api/parties";
import { notify } from "@/shared/feedback/notify";
import { queryKeys } from "@/shared/query/keys";
import { contactGlanceStats } from "../contact-glance-math";

/**
 * Desk side glance — party facts · View more (plan/45).
 * No Edit. Prefetches full detail so View more feels instant.
 */
export function ContactGlancePeek(props: {
  open: boolean;
  partyId: string | null;
  companySlug: string;
  listPath: string;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const partyId = props.partyId;

  const detailQuery = useQuery({
    queryKey: queryKeys.parties.detail(props.companySlug, partyId ?? ""),
    enabled: props.open && Boolean(partyId),
    queryFn: () => getParty(props.companySlug, partyId!),
  });

  useEffect(() => {
    if (!props.open || !partyId) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.parties.detail(props.companySlug, partyId),
      queryFn: () => getParty(props.companySlug, partyId),
    });
  }, [props.open, partyId, props.companySlug, queryClient]);

  const party = detailQuery.data?.party ?? null;
  const stats = party ? contactGlanceStats(party) : null;
  const title = party?.name ?? "Contact";

  function viewMore() {
    if (!partyId) return;
    stashListReturn(props.listPath, partyId);
    props.onOpenChange(false);
    navigate(`/c/${props.companySlug}/contacts/${partyId}`);
  }

  async function copyPhone() {
    const phone = stats?.primaryPhone?.trim();
    if (!phone) return;
    try {
      await navigator.clipboard.writeText(phone);
      notify.info(`Copied ${phone}`);
    } catch {
      notify.error("Could not copy phone");
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className="fixed inset-y-0 right-0 left-auto top-0 z-50 flex h-dvh max-h-dvh w-[min(100%,22.5rem)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 border-l border-[var(--color-border)] p-0 shadow-[var(--shadow-float)]"
        aria-describedby={undefined}
      >
        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base">{title}</DialogTitle>
            <DialogDescription className="mt-0.5 text-xs text-[var(--color-muted)]">
              Glance · read only
            </DialogDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => props.onOpenChange(false)}
          >
            Close
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
          {detailQuery.isPending && !party ? (
            <Alert variant="muted">Loading…</Alert>
          ) : detailQuery.isError ? (
            <Alert variant="destructive">
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : "Failed to load"}
            </Alert>
          ) : party && stats ? (
            <>
              <DetailReadout
                className="mt-0"
                rows={[
                  { label: "Name", value: party.name },
                  ...(stats.code
                    ? [{ label: "Code", value: stats.code, mono: true as const }]
                    : []),
                  { label: "Roles", value: stats.roleLabel },
                  ...(stats.deactivated
                    ? [{ label: "Status", value: "Deactivated" }]
                    : []),
                  {
                    label: "Phone",
                    value: stats.primaryPhone ?? "—",
                    mono: Boolean(stats.primaryPhone),
                  },
                  {
                    label: "Email",
                    value: stats.primaryEmail ?? "—",
                  },
                  ...(stats.outstandingAr
                    ? [
                        {
                          label: "AR open",
                          value: `₱${stats.outstandingAr}`,
                          mono: true as const,
                        },
                      ]
                    : []),
                  ...(stats.outstandingAp
                    ? [
                        {
                          label: "AP open",
                          value: `₱${stats.outstandingAp}`,
                          mono: true as const,
                        },
                      ]
                    : []),
                  ...((party.partyKind ?? "org") === "org"
                    ? [
                        {
                          label: "People",
                          value:
                            stats.peopleCount === 0
                              ? "None linked"
                              : `${stats.peopleCount} linked`,
                        },
                      ]
                    : []),
                  ...(stats.notesPreview
                    ? [{ label: "Notes", value: stats.notesPreview }]
                    : []),
                ]}
              />
              <div className="mt-6 flex flex-col gap-2">
                <Button type="button" onClick={() => viewMore()}>
                  View more
                </Button>
                {stats.primaryPhone ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void copyPhone()}
                  >
                    Copy phone
                  </Button>
                ) : null}
                {party.isCustomer || party.isSupplier ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (!partyId) return;
                      props.onOpenChange(false);
                      navigate(
                        `/c/${props.companySlug}/contacts/${partyId}/statement`,
                      );
                    }}
                  >
                    Print statement
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
